import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';

async function fetchAllLines(asOf, branchId = null) {
  const PAGE = 1000;
  let all = [], from = 0;
  while (true) {
    let q = supabase
      .from('journal_lines')
      .select('base_debit, base_credit, account:account_id(id, account_code, account_name, account_types(category)), journal_entries!inner(status, entry_date)')
      .eq('journal_entries.status', 'POSTED')
      .lte('journal_entries.entry_date', asOf)
      .range(from, from + PAGE - 1);
    if (branchId) q = q.eq('journal_entries.branch_id', branchId);
    const { data } = await q;
    if (!data || !data.length) break;
    all = all.concat(data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

export default function BalanceSheet() {
  const navigate = useNavigate();
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [equity, setEquity] = useState([]);
  const [retainedEarnings, setRetainedEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');

  useEffect(() => {
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);

    const map = {};
    let totalIncome = 0, totalExpense = 0;

    const lines = await fetchAllLines(asOf, branchFilter || null);
    if (lines.length) {
      lines.forEach(l => {
        const acc = l.account;
        if (!acc) return;
        const cat = acc.account_types?.category;
        const dr = parseFloat(l.base_debit || 0);
        const cr = parseFloat(l.base_credit || 0);
        if (cat === 'INCOME')  { totalIncome  += (cr - dr); return; }
        if (cat === 'EXPENSE') { totalExpense += (dr - cr); return; }
        if (!map[acc.account_code]) map[acc.account_code] = { id: acc.id, code: acc.account_code, name: acc.account_name, category: cat, balance: 0 };
        if (cat === 'ASSET') map[acc.account_code].balance += (dr - cr);
        else                 map[acc.account_code].balance += (cr - dr);
      });
    }

    const all = Object.values(map).sort((a, b) => a.code.localeCompare(b.code));
    setAssets(all.filter(r => r.category === 'ASSET' && r.balance !== 0));
    setLiabilities(all.filter(r => r.category === 'LIABILITY' && r.balance !== 0));
    setEquity(all.filter(r => r.category === 'EQUITY' && r.balance !== 0));
    setRetainedEarnings(totalIncome - totalExpense);
    setLoading(false);
  }, [asOf, branchFilter]);

  useEffect(() => { load(); }, [load]);

  const totalAssets       = assets.reduce((s, r) => s + r.balance, 0);
  const totalLiabilities  = liabilities.reduce((s, r) => s + r.balance, 0);
  const totalEquity       = equity.reduce((s, r) => s + r.balance, 0) + retainedEarnings;
  const totalLiabEquity   = totalLiabilities + totalEquity;
  const diff              = totalAssets - totalLiabEquity;
  const isBalanced        = Math.abs(diff) < 1;

  const Section = ({ title, rows, total, color }) => (
    <div className="card">
      <div className="card-header">
        <div className="card-title">{title}</div>
        <div className="amount" style={{ fontWeight: 700, color }}>{total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
      </div>
      <div className="table-wrapper">
        <table>
          <thead><tr><th>Code</th><th>Account</th><th style={{ textAlign: 'right' }}>Balance</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.code} onClick={() => r.id && navigate(`/accounts/${r.id}/ledger?to=${asOf}`)} style={{ cursor: r.id ? 'pointer' : 'default' }}>
                <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{r.code}</td>
                <td>{r.name}</td>
                <td style={{ textAlign: 'right', fontWeight: 500 }} className="amount">{r.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 12 }}>None</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Balance Sheet</div><div className="page-subtitle">All figures from double-entry journals</div></div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div>
            <label className="form-label">Branch</label>
            <select className="form-input" value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
              <option value="">All Branches</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div><label className="form-label">As of Date</label><input className="form-input" type="date" value={asOf} onChange={e => setAsOf(e.target.value)} /></div>
          <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Total Assets</div><div className="stat-value amount">{totalAssets.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
        <div className="stat-card"><div className="stat-label">Total Liabilities</div><div className="stat-value amount">{totalLiabilities.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
        <div className="stat-card"><div className="stat-label">Total Equity</div><div className="stat-value amount">{totalEquity.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
        <div className="stat-card" style={{ borderLeft: `3px solid ${isBalanced ? 'var(--success)' : 'var(--danger)'}` }}>
          <div className="stat-label">{isBalanced ? '✓ Balanced' : '⚠ Off by'}</div>
          <div className="stat-value amount" style={{ color: isBalanced ? 'var(--success)' : 'var(--danger)' }}>
            {isBalanced ? 'Assets = L + E' : Math.abs(diff).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
        </div>
      </div>

      {loading ? <div className="spinner" /> : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Section title="Assets" rows={assets} total={totalAssets} color="var(--primary)" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Section title="Liabilities" rows={liabilities} total={totalLiabilities} color="var(--danger)" />
            <div className="card">
              <div className="card-header">
                <div className="card-title">Equity</div>
                <div className="amount" style={{ fontWeight: 700, color: 'var(--success)' }}>{totalEquity.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Code</th><th>Account</th><th style={{ textAlign: 'right' }}>Balance</th></tr></thead>
                  <tbody>
                    {equity.map(r => (
                      <tr key={r.code} onClick={() => r.id && navigate(`/accounts/${r.id}/ledger?to=${asOf}`)} style={{ cursor: r.id ? 'pointer' : 'default' }}>
                        <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{r.code}</td>
                        <td>{r.name}</td>
                        <td style={{ textAlign: 'right', fontWeight: 500 }} className="amount">{r.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                    {retainedEarnings !== 0 && (
                      <tr style={{ background: 'var(--bg)' }}>
                        <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>RE</td>
                        <td>Retained Earnings (Current Period)</td>
                        <td style={{ textAlign: 'right', fontWeight: 500, color: retainedEarnings >= 0 ? 'var(--success)' : 'var(--danger)' }} className="amount">
                          {retainedEarnings < 0 ? '(' : ''}{Math.abs(retainedEarnings).toLocaleString(undefined, { maximumFractionDigits: 0 })}{retainedEarnings < 0 ? ')' : ''}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card" style={{ borderTop: `3px solid ${isBalanced ? 'var(--success)' : 'var(--danger)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700 }}>Total Liabilities + Equity</div>
                <div className="amount" style={{ fontSize: 20, fontWeight: 700 }}>{totalLiabEquity.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
